import { R2_MINIMAL_STEP_SECONDS, scheduleR2MinimalMusicStep } from './r2/NearFutureMusicProfile';
import { HtmlAudioFallback, needsHtmlAudioFallback } from './HtmlAudioFallback';

interface SfxChannel {
  readonly gain: GainNode;
  busyUntil: number;
  priority: number;
}

const LEAD_PATTERN = new Int8Array([0, -1, 7, -1, 10, -1, 7, -1, 3, -1, 12, -1, 10, 7, -1, -1]);
const LEAD_LAYER_2 = new Int8Array([12, -1, 15, -1, 19, -1, 15, -1, 10, -1, 17, -1, 15, 12, -1, -1]);
const BASS_PATTERN = new Int8Array([0, -1, -1, 0, 5, -1, -1, 5, 3, -1, -1, 3, 7, -1, 5, -1]);
const PULSE_PATTERN = new Int8Array([0, 12, 7, 12, 3, 12, 7, 15, 0, 12, 10, 12, 7, 15, 10, 19]);
const ROOTS = new Int8Array([45, 48, 43, 41]);
/** Stage 1→4のBGMバス増幅。曲の要素追加だけでなく、床の音圧そのものを段階解放する。 */
const R2_STAGE_MUSIC_LIFT = new Float32Array([1, 1.16, 1.33, 1.42]);
const R2_BOSS_MUSIC_LIFT = 1.12;

/** id → carrier frequency (Hz). Fix 4: hoisted out of sfxFrequency() so it isn't
 *  reallocated on every playSfx call. */
const SFX_FREQUENCIES = [920, 680, 115, 170, 88, 240, 1020, 760, 520, 190, 340, 410, 150, 740, 92, 220, 620, 980, 120, 1180];

/** playSfx() is fully deterministic per id (fixed frequency/duration table,
 *  no per-call randomization), so each id can be pre-rendered exactly once. */
const sfxDuration = (id: number): number => (id === 14 ? 0.9 : id === 15 ? 0.5 : id === 5 ? 0.32 : 0.14);
const sfxUsesNoise = (id: number): boolean => id === 2 || id === 4 || id === 14 || id === 15;
const sfxNoiseLevel = (id: number): number => (id === 14 ? 0.58 : 0.28);

/** Pure synthesis helpers shared by the live AudioContext path and the
 *  OfflineAudioContext pre-render path (Fix 2) — identical parameters, so the
 *  offline-rendered buffer is byte-for-byte what the live graph used to produce. */
function synthTone(context: BaseAudioContext, destination: AudioNode, time: number, duration: number, frequency: number, id: number): void {
  const oscillator = context.createOscillator();
  oscillator.type = id === 1 ? 'square' : id === 6 ? 'sine' : 'triangle';
  oscillator.frequency.setValueAtTime(frequency, time);
  if (id === 14) oscillator.frequency.exponentialRampToValueAtTime(44, time + duration);
  else if (id === 15) oscillator.frequency.exponentialRampToValueAtTime(Math.max(32, frequency * 0.28), time + duration);
  else oscillator.frequency.exponentialRampToValueAtTime(Math.max(50, frequency * (id === 0 ? 1.4 : 0.78)), time + duration);
  oscillator.connect(destination);
  oscillator.start(time);
  oscillator.stop(time + duration + 0.01);
}

function synthNoise(context: BaseAudioContext, noiseBuffer: AudioBuffer, destination: AudioNode, time: number, duration: number, level: number): void {
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const envelope = context.createGain();
  source.buffer = noiseBuffer;
  filter.type = duration < 0.06 ? 'highpass' : 'bandpass';
  filter.frequency.value = duration < 0.06 ? 6500 : 1600;
  filter.Q.value = 0.8;
  envelope.gain.setValueAtTime(Math.max(0.0001, level), time);
  envelope.gain.exponentialRampToValueAtTime(0.0001, time + duration);
  source.connect(filter);
  filter.connect(envelope);
  envelope.connect(destination);
  source.start(time);
  source.stop(time + duration + 0.01);
}

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
}

export type MusicProfile = 'legacy' | 'r2-minimal';

export class AudioEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private noise: AudioBuffer | null = null;
  private mediaPrimer: HTMLAudioElement | null = null;
  private mediaCarrier: HTMLAudioElement | null = null;
  private readonly htmlAudioFallback: HtmlAudioFallback | null;
  private outputVerified = false;
  private disposed = false;
  private startGeneration = 0;
  private musicSchedulerTimer: number | null = null;
  /** Fix 2: id → pre-rendered SFX buffer. Populated asynchronously by primeSfxBuffers();
   *  playSfx() falls back to live synthesis for any id not yet cached. */
  private readonly sfxBufferCache = new Map<number, AudioBuffer>();
  private sfxBuffersPrimed = false;
  private readonly channels: SfxChannel[] = [];
  private nextStepTime = 0;
  private step = 0;
  private currentLayer = 0;
  private pendingLayer = 0;
  private currentStage = 0;
  private pendingStage = 0;
  private paused = false;
  private bossActive = false;
  private musicGridOrigin = 0;
  private settings: AudioSettings = { master: 0.78, music: 0.68, sfx: 0.82 };
  private readonly musicProfile: MusicProfile;
  private readonly stepSeconds: number;
  onStateChange: ((state: string) => void) | null = null;

  constructor(musicProfile: MusicProfile = 'legacy') {
    this.musicProfile = musicProfile;
    this.stepSeconds = musicProfile === 'r2-minimal' ? R2_MINIMAL_STEP_SECONDS : 60 / 132 / 4;
    if (musicProfile === 'r2-minimal') this.settings = { master: 0.78, music: 0.82, sfx: 0.7 };
    this.htmlAudioFallback = musicProfile === 'r2-minimal' && needsHtmlAudioFallback()
      ? new HtmlAudioFallback()
      : null;
    if (!this.htmlAudioFallback && typeof Audio !== 'undefined') {
      const primer = new Audio('/assets/audio/r2/player-shot-a-01.wav');
      primer.preload = 'auto';
      primer.setAttribute('playsinline', '');
      this.mediaPrimer = primer;
      const carrier = new Audio('/assets/audio/r2/ios-audio-carrier.wav');
      carrier.preload = 'auto';
      carrier.loop = true;
      carrier.setAttribute('playsinline', '');
      this.mediaCarrier = carrier;
    }
  }

  get state(): string {
    if (this.htmlAudioFallback) return this.htmlAudioFallback.state;
    return this.context?.state ?? 'suspended';
  }

  get ready(): boolean {
    if (this.htmlAudioFallback) return this.htmlAudioFallback.ready;
    return this.outputVerified && this.context?.state === 'running';
  }

  get usesHTMLMediaFallback(): boolean {
    return this.htmlAudioFallback !== null;
  }

  /**
   * A previously unlocked output can still lose its media route on iPhone.
   * App capture uses this only to permit a fresh real-gesture retry; an
   * intentional game pause does not request another unlock.
   */
  get needsUserGesture(): boolean {
    if (this.htmlAudioFallback) return this.htmlAudioFallback.needsUserGesture;
    return Boolean(
      this.outputVerified
      && !this.paused
      && this.context
      && this.context.state !== 'running',
    );
  }

  /** Fix 1: lets ComboFeelAdapter hand the same AudioContext to CombatAudioEngine
   *  instead of it opening a second one. null until ensureStarted() has run. */
  getContext(): AudioContext | null {
    return this.context;
  }

  /**
   * R2 上物専用の読み取り窓口。操作SEは即時発音し、追加アクセントだけを
   * この時刻へ置く。legacy profile / pause / 未解錠時は null。
   */
  nextR2MusicGridTime(stepMultiple = 1, minLead = .012): number | null {
    const context = this.context;
    if (
      this.musicProfile !== 'r2-minimal'
      || !context
      || context.state !== 'running'
      || this.paused
      || this.musicGridOrigin <= 0
    ) return null;
    const multiple = Math.max(1, stepMultiple | 0);
    const gridSeconds = this.stepSeconds * multiple;
    const after = context.currentTime + Math.max(0, minLead);
    const gridIndex = Math.max(0, Math.ceil((after - this.musicGridOrigin) / gridSeconds - 1e-7));
    return this.musicGridOrigin + gridIndex * gridSeconds;
  }

  /** R2描画演出専用。現在位置を16分音符単位の連続値で返し、音声ノードは公開しない。 */
  r2MusicStepPosition(): number {
    if (this.htmlAudioFallback) return this.htmlAudioFallback.stepPosition();
    const context = this.context;
    if (
      this.musicProfile !== 'r2-minimal'
      || !context
      || context.state !== 'running'
      || this.paused
      || this.musicGridOrigin <= 0
    ) return -1;
    return Math.max(0, (context.currentTime - this.musicGridOrigin) / this.stepSeconds);
  }

  async ensureStarted(): Promise<void> {
    if (this.disposed) return;
    const generation = ++this.startGeneration;
    if (this.htmlAudioFallback) {
      await this.htmlAudioFallback.ensureStarted();
      if (this.disposed || generation !== this.startGeneration) return;
      // The fallback unlocks its media inside the user gesture. Reapply the
      // runtime pause after that gesture so a foreground SOUND ON tap cannot
      // make music escape from an intentionally paused game.
      if (this.paused) this.htmlAudioFallback.setPaused(true);
      this.outputVerified = true;
      this.onStateChange?.(this.htmlAudioFallback.state);
      return;
    }
    if (!this.context) this.initialize();
    const context = this.context;
    if (!context) throw new Error('Web Audio API is unavailable.');
    // Start an HTML media element synchronously from the tap as well. This
    // establishes iOS's media output session before Web Audio resumes and
    // gives the player a short, audible confirmation that SOUND ON worked.
    this.primeIOSMediaSession();
    const clockBefore = context.currentTime;
    if (context.state !== 'running') {
      // iOS Safari is most reliable when resume() and a source start both
      // happen synchronously inside the same user gesture. The one-frame
      // zero buffer is inaudible; it only wakes the hardware output path.
      const resume = context.resume();
      this.wakeAudioOutput(context);
      this.playUnlockConfirmation(context);
      await resume;
      if (this.disposed || generation !== this.startGeneration) return;
    } else {
      this.wakeAudioOutput(context);
      this.playUnlockConfirmation(context);
    }
    if (context.state !== 'running') {
      throw new Error('Audio is still suspended. Tap SOUND ON again.');
    }
    // WebKit can report "running" while currentTime is frozen and output is
    // silent after a foreground/route transition. Detect that false-positive,
    // discard the dead graph, and let the next tap construct a fresh context.
    await new Promise<void>((resolve) => window.setTimeout(resolve, 96));
    if (this.disposed || generation !== this.startGeneration) return;
    if (context.state !== 'running' || context.currentTime - clockBefore < 0.02) {
      this.resetAudioGraph(context);
      throw new Error('Audio output stalled. Tap SOUND ON once more.');
    }
    this.outputVerified = true;
    this.nextStepTime = context.currentTime + 0.04;
    this.alignMusicGridOrigin();
    this.startMusicScheduler();
    this.onStateChange?.(context.state);
  }

  setSettings(settings: AudioSettings): void {
    this.settings = settings;
    this.htmlAudioFallback?.setSettings(settings);
    if (!this.context || !this.master || !this.musicBus || !this.sfxBus) return;
    const now = this.context.currentTime;
    this.master.gain.setTargetAtTime(Math.max(0, Math.min(1, settings.master)), now, 0.02);
    this.musicBus.gain.setTargetAtTime(this.musicTargetLevel(), now, 0.02);
    this.sfxBus.gain.setTargetAtTime(Math.max(0, Math.min(1, settings.sfx)), now, 0.02);
  }

  /** Decode/buffer the next iPhone media track while StageResult owns the frame. */
  prepareStageMusic(stage: number): void {
    this.htmlAudioFallback?.prepareStageMusic(stage);
  }

  setPaused(value: boolean): void {
    this.paused = value;
    this.htmlAudioFallback?.setPaused(value);
    if (this.htmlAudioFallback) return;
    if (!this.context || !this.musicBus) return;
    const now = this.context.currentTime;
    this.musicBus.gain.cancelScheduledValues(now);
    this.musicBus.gain.setTargetAtTime(value ? 0.0001 : this.musicTargetLevel(), now, 0.03);
    if (!value) {
      this.nextStepTime = Math.max(this.nextStepTime, now + 0.03);
      this.alignMusicGridOrigin();
    }
  }

  update(layer: number, boss: boolean, stage = 0): void {
    if (this.htmlAudioFallback) {
      this.htmlAudioFallback.update(layer, boss, stage);
      return;
    }
    const context = this.context;
    if (!context || context.state !== 'running' || this.paused) return;
    this.pendingLayer = Math.max(0, Math.min(3, layer | 0));
    this.pendingStage = Math.max(0, Math.min(3, stage | 0));
    if (boss !== this.bossActive) {
      this.bossActive = boss;
      this.duck(boss ? 0.72 : 0.92, boss ? 0.12 : 0.22);
    }
    this.scheduleMusicHorizon();
  }

  private scheduleMusicHorizon(): void {
    const context = this.context;
    if (!context || context.state !== 'running' || this.paused) return;
    const horizon = context.currentTime + 0.28;
    if (this.nextStepTime < context.currentTime - 0.25) {
      this.nextStepTime = context.currentTime + 0.02;
      this.alignMusicGridOrigin();
    }
    while (this.nextStepTime < horizon) {
      if ((this.step & 15) === 0) {
        this.currentLayer = this.pendingLayer;
        if (this.currentStage !== this.pendingStage) {
          this.currentStage = this.pendingStage;
          this.musicBus?.gain.setTargetAtTime(this.musicTargetLevel(), this.nextStepTime, 0.08);
        }
      }
      if (this.musicProfile === 'r2-minimal' && this.musicBus && this.noise) {
        scheduleR2MinimalMusicStep(
          context,
          this.musicBus,
          this.noise,
          this.step,
          this.nextStepTime,
          this.currentLayer,
          this.bossActive,
          this.currentStage,
        );
      } else {
        this.scheduleStep(this.step, this.nextStepTime);
      }
      this.step = (this.step + 1) & 63;
      this.nextStepTime += this.stepSeconds;
    }
  }

  /**
   * Fix 2: the audible shape is `channelEnvelope(t) * bufferContent(t)` either way, so
   * playing a pre-rendered buffer through the reused per-channel GainNode (with the
   * SAME live envelope scheduling as before) is bit-for-bit identical to the old
   * per-call oscillator/noise graph — it just replaces 2-4 synth nodes with 1
   * AudioBufferSourceNode. Channel-stealing behavior (cancelScheduledValues cutting
   * off whatever was previously routed through this channel) is preserved because
   * the envelope is still scheduled live on channel.gain, not baked into the buffer.
   */
  playSfx(id: number): void {
    if (this.htmlAudioFallback) {
      this.htmlAudioFallback.playSfx(id);
      return;
    }
    const context = this.context;
    if (!context || context.state !== 'running' || !this.sfxBus) return;
    const priority = id === 14 || id === 15 ? 4 : id >= 12 ? 3 : id >= 8 ? 2 : 1;
    const channel = this.acquireChannel(priority, context.currentTime);
    if (!channel) return;
    const duration = sfxDuration(id);
    channel.busyUntil = context.currentTime + duration;
    channel.priority = priority;
    channel.gain.gain.cancelScheduledValues(context.currentTime);
    channel.gain.gain.setValueAtTime(0.0001, context.currentTime);
    channel.gain.gain.exponentialRampToValueAtTime(id === 1 ? 0.08 : 0.18, context.currentTime + 0.006);
    channel.gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + duration);
    const buffer = this.sfxBufferCache.get(id);
    if (buffer) {
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(channel.gain);
      source.start(context.currentTime);
      source.stop(context.currentTime + duration + 0.02);
    } else {
      // Cache not warmed yet (rare: only in the brief window right after unlock) —
      // fall back to the original per-call synthesis so playback never drops out.
      if (sfxUsesNoise(id) && this.noise) synthNoise(context, this.noise, channel.gain, context.currentTime, duration, sfxNoiseLevel(id));
      synthTone(context, channel.gain, context.currentTime, duration, this.sfxFrequency(id), id);
    }
    if (id === 12 || id === 14 || id === 15) this.duck(id === 14 ? 0.3 : 0.52, id === 14 ? 0.8 : 0.35);
  }

  setPlayerFireHeld(active: boolean): void {
    this.htmlAudioFallback?.setPlayerFireHeld(active);
  }

  playMobileCombo(index: number): void {
    this.htmlAudioFallback?.playCombo(index);
  }

  /**
   * r2 経路専用。SE をリッチ側（CombatAudioEngine）が鳴らした場合、ここは BGM
   * だけを鳴らし続けているため、重い演出に合わせた音楽ダッキングを外から与える。
   */
  duckMusicExternal(target: number, duration: number, when?: number): void {
    if (this.htmlAudioFallback) {
      this.htmlAudioFallback.duck(target, duration);
      return;
    }
    this.duck(target, duration, when);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.startGeneration += 1;
    this.onStateChange = null;
    this.htmlAudioFallback?.dispose();
    const context = this.context;
    this.context = null;
    this.master = null;
    this.musicBus = null;
    this.sfxBus = null;
    this.compressor = null;
    this.noise = null;
    this.sfxBufferCache.clear();
    this.sfxBuffersPrimed = false;
    this.channels.length = 0;
    this.outputVerified = false;
    if (this.musicSchedulerTimer !== null) {
      window.clearInterval(this.musicSchedulerTimer);
      this.musicSchedulerTimer = null;
    }
    if (this.mediaPrimer) {
      this.mediaPrimer.pause();
      this.mediaPrimer.removeAttribute('src');
      this.mediaPrimer = null;
    }
    if (this.mediaCarrier) {
      this.mediaCarrier.pause();
      this.mediaCarrier.removeAttribute('src');
      this.mediaCarrier = null;
    }
    if (context) void context.close();
  }

  private initialize(): void {
    const AudioCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return;
    // Do not force 48 kHz here. Some iPhones expose a 44.1 kHz hardware route
    // and reject an AudioContext whose requested sample rate cannot be created.
    // Decoded 48 kHz one-shots are resampled by Web Audio automatically.
    const context = new AudioCtor({ latencyHint: 'interactive' });
    this.outputVerified = false;
    const master = context.createGain();
    const musicBus = context.createGain();
    const sfxBus = context.createGain();
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -16;
    compressor.knee.value = 18;
    compressor.ratio.value = 5;
    compressor.attack.value = 0.004;
    compressor.release.value = 0.18;
    musicBus.connect(master);
    sfxBus.connect(master);
    master.connect(compressor);
    compressor.connect(context.destination);
    master.gain.value = this.settings.master;
    musicBus.gain.value = this.musicTargetLevel();
    sfxBus.gain.value = this.settings.sfx;
    this.context = context;
    this.master = master;
    this.musicBus = musicBus;
    this.sfxBus = sfxBus;
    this.compressor = compressor;
    this.noise = this.makeNoiseBuffer(context);
    for (let i = 0; i < 12; i += 1) {
      const gain = context.createGain();
      gain.gain.value = 0.0001;
      gain.connect(sfxBus);
      this.channels.push({ gain, busyUntil: 0, priority: 0 });
    }
    context.onstatechange = () => this.onStateChange?.(context.state);
    this.primeSfxBuffers(context);
  }

  private wakeAudioOutput(context: AudioContext): void {
    const source = context.createBufferSource();
    source.buffer = context.createBuffer(1, 1, context.sampleRate);
    source.connect(context.destination);
    source.start(0);
  }

  private playUnlockConfirmation(context: AudioContext): void {
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.frequency.exponentialRampToValueAtTime(1320, now + 0.045);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.035, now + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.075);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.08);
  }

  private primeIOSMediaSession(): void {
    const primer = this.mediaPrimer;
    if (primer) {
      primer.pause();
      primer.currentTime = 0;
      primer.volume = Math.max(0.08, Math.min(0.22, this.settings.master * 0.18));
      void primer.play().catch(() => undefined);
    }
    const carrier = this.mediaCarrier;
    if (carrier) {
      carrier.volume = 1;
      void carrier.play().catch(() => undefined);
    }
  }

  private resetAudioGraph(context: AudioContext): void {
    if (this.context !== context) return;
    context.onstatechange = null;
    this.context = null;
    this.master = null;
    this.musicBus = null;
    this.sfxBus = null;
    this.compressor = null;
    this.noise = null;
    this.sfxBufferCache.clear();
    this.sfxBuffersPrimed = false;
    this.channels.length = 0;
    this.outputVerified = false;
    if (this.musicSchedulerTimer !== null) {
      window.clearInterval(this.musicSchedulerTimer);
      this.musicSchedulerTimer = null;
    }
    void context.close().catch(() => undefined);
    this.onStateChange?.('suspended');
  }

  private startMusicScheduler(): void {
    if (this.musicSchedulerTimer === null) {
      this.musicSchedulerTimer = window.setInterval(() => this.scheduleMusicHorizon(), 25);
    }
    // Do not wait for either the render loop or the first interval edge. The
    // first kick is scheduled as part of the successful SOUND ON gesture.
    this.scheduleMusicHorizon();
  }

  private scheduleStep(step: number, time: number): void {
    const root = ROOTS[(step >> 4) & 3] ?? 45;
    const within = step & 15;
    if ((within & 3) === 0) this.kick(time, within === 0 ? 0.22 : 0.14);
    if (within === 4 || within === 12) this.snare(time, this.currentLayer >= 2 ? 0.12 : 0.08);
    if ((within & 1) === 0) this.hat(time, this.currentLayer >= 1 ? 0.045 : 0.025);
    const bass = BASS_PATTERN[within] ?? -1;
    if (bass >= 0) this.fmBass(time, root + bass, this.stepSeconds * 1.8, this.currentLayer >= 1 ? 0.12 : 0.085);
    if (this.currentLayer >= 1) {
      const leadPattern = this.currentLayer >= 3 ? LEAD_LAYER_2 : LEAD_PATTERN;
      const lead = leadPattern[within] ?? -1;
      if (lead >= 0) this.fmLead(time, root + 12 + lead, this.stepSeconds * 1.55, this.currentLayer >= 3 ? 0.085 : 0.06);
    }
    if (this.currentLayer >= 2) {
      const pulse = PULSE_PATTERN[within] ?? 0;
      this.pulse(time, root + 24 + pulse, this.stepSeconds * 0.72, this.currentLayer >= 3 ? 0.038 : 0.025);
    }
  }

  private fmLead(time: number, midi: number, duration: number, level: number): void {
    const context = this.context;
    const bus = this.musicBus;
    if (!context || !bus) return;
    const carrier = context.createOscillator();
    const modulator = context.createOscillator();
    const modGain = context.createGain();
    const envelope = context.createGain();
    const frequency = this.midiToHz(midi);
    carrier.type = 'sine';
    carrier.frequency.setValueAtTime(frequency, time);
    modulator.type = 'sine';
    modulator.frequency.setValueAtTime(frequency * 2.01, time);
    modGain.gain.setValueAtTime(frequency * 1.25, time);
    modGain.gain.exponentialRampToValueAtTime(Math.max(1, frequency * 0.08), time + duration);
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.exponentialRampToValueAtTime(level, time + 0.012);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    modulator.connect(modGain);
    modGain.connect(carrier.frequency);
    carrier.connect(envelope);
    envelope.connect(bus);
    modulator.start(time);
    carrier.start(time);
    modulator.stop(time + duration + 0.02);
    carrier.stop(time + duration + 0.02);
  }

  private fmBass(time: number, midi: number, duration: number, level: number): void {
    const context = this.context;
    const bus = this.musicBus;
    if (!context || !bus) return;
    const carrier = context.createOscillator();
    const modulator = context.createOscillator();
    const modGain = context.createGain();
    const envelope = context.createGain();
    const frequency = this.midiToHz(midi);
    carrier.type = 'triangle';
    carrier.frequency.setValueAtTime(frequency, time);
    modulator.type = 'sine';
    modulator.frequency.setValueAtTime(frequency * 1.005, time);
    modGain.gain.setValueAtTime(frequency * 0.9, time);
    modGain.gain.exponentialRampToValueAtTime(1, time + duration);
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.exponentialRampToValueAtTime(level, time + 0.008);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    modulator.connect(modGain);
    modGain.connect(carrier.frequency);
    carrier.connect(envelope);
    envelope.connect(bus);
    modulator.start(time);
    carrier.start(time);
    modulator.stop(time + duration + 0.02);
    carrier.stop(time + duration + 0.02);
  }

  private pulse(time: number, midi: number, duration: number, level: number): void {
    const context = this.context;
    const bus = this.musicBus;
    if (!context || !bus) return;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(this.midiToHz(midi), time);
    envelope.gain.setValueAtTime(0.0001, time);
    envelope.gain.exponentialRampToValueAtTime(level, time + 0.003);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    oscillator.connect(envelope);
    envelope.connect(bus);
    oscillator.start(time);
    oscillator.stop(time + duration + 0.01);
  }

  private kick(time: number, level: number): void {
    const context = this.context;
    const bus = this.musicBus;
    if (!context || !bus) return;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(112, time);
    oscillator.frequency.exponentialRampToValueAtTime(38, time + 0.11);
    envelope.gain.setValueAtTime(level, time);
    envelope.gain.exponentialRampToValueAtTime(0.0001, time + 0.13);
    oscillator.connect(envelope);
    envelope.connect(bus);
    oscillator.start(time);
    oscillator.stop(time + 0.14);
  }

  private snare(time: number, level: number): void {
    if (!this.musicBus) return;
    this.playNoise(this.musicBus, time, 0.11, level);
  }

  private hat(time: number, level: number): void {
    if (!this.musicBus) return;
    this.playNoise(this.musicBus, time, 0.035, level);
  }

  /** Used by the music sequencer (kick/snare/hat/...), which is unaffected by Fix 2 —
   *  only playSfx()'s per-call node churn was the hot-path GC concern. */
  private playNoise(destination: AudioNode, time: number, duration: number, level: number): void {
    const context = this.context;
    const noise = this.noise;
    if (!context || !noise) return;
    synthNoise(context, noise, destination, time, duration, level);
  }

  private playTone(destination: AudioNode, time: number, duration: number, frequency: number, id: number): void {
    const context = this.context;
    if (!context) return;
    synthTone(context, destination, time, duration, frequency, id);
  }

  /**
   * Fix 2: renders every SFX id once into an AudioBuffer via OfflineAudioContext,
   * using the exact same synthesis parameters as the live path (synthTone / synthNoise
   * above), then caches it. playSfx() plays the cached buffer through a single
   * AudioBufferSourceNode instead of rebuilding the oscillator/noise/filter graph
   * on every call — the source of the GC-pressure node churn.
   */
  private primeSfxBuffers(context: AudioContext): void {
    if (this.sfxBuffersPrimed) return;
    // R2 gameplay owns its one-shots in CombatAudioEngine. Rendering twenty
    // legacy OfflineAudioContexts during iPhone unlock only competes with the
    // live music context; the rare fallback can use the existing live path.
    if (this.musicProfile === 'r2-minimal') return;
    this.sfxBuffersPrimed = true;
    void this.primeSfxBuffersSequentially(context);
  }

  private async primeSfxBuffersSequentially(context: AudioContext): Promise<void> {
    for (let id = 0; id < SFX_FREQUENCIES.length; id += 1) {
      if (this.context !== context) return;
      const buffer = await this.renderSfxBuffer(context, id);
      if (buffer) this.sfxBufferCache.set(id, buffer);
    }
  }

  private async renderSfxBuffer(liveContext: AudioContext, id: number): Promise<AudioBuffer | null> {
    const noise = this.noise;
    const duration = sfxDuration(id);
    const sampleRate = liveContext.sampleRate;
    const length = Math.max(1, Math.ceil((duration + 0.03) * sampleRate));
    const offline = new OfflineAudioContext(1, length, sampleRate);
    // Deliberately NOT baking the channel-envelope shape here: playSfx() still
    // schedules that live on the reused channel GainNode, so channel-stealing
    // (cancelScheduledValues cutting off a still-playing sound) keeps working.
    synthTone(offline, offline.destination, 0, duration, SFX_FREQUENCIES[id] ?? 440, id);
    if (sfxUsesNoise(id) && noise) synthNoise(offline, noise, offline.destination, 0, duration, sfxNoiseLevel(id));
    try {
      return await offline.startRendering();
    } catch {
      return null;
    }
  }

  private acquireChannel(priority: number, now: number): SfxChannel | null {
    let candidate: SfxChannel | null = null;
    for (let i = 0; i < this.channels.length; i += 1) {
      const channel = this.channels[i]!;
      if (channel.busyUntil <= now) return channel;
      if (channel.priority <= priority && (!candidate || channel.priority < candidate.priority || channel.busyUntil < candidate.busyUntil)) candidate = channel;
    }
    return candidate;
  }

  private alignMusicGridOrigin(): void {
    this.musicGridOrigin = this.nextStepTime - this.step * this.stepSeconds;
  }

  private musicTargetLevel(): number {
    if (this.musicProfile !== 'r2-minimal') return Math.max(0, Math.min(1, this.settings.music));
    const lift = R2_STAGE_MUSIC_LIFT[this.currentStage] ?? R2_STAGE_MUSIC_LIFT[0]!;
    const bossLift = this.bossActive ? R2_BOSS_MUSIC_LIFT : 1;
    return Math.max(0, Math.min(1.2, this.settings.music * lift * bossLift));
  }

  private duck(target: number, duration: number, when?: number): void {
    const context = this.context;
    const bus = this.musicBus;
    if (!context || !bus) return;
    const startAt = Math.max(context.currentTime, when ?? context.currentTime);
    bus.gain.cancelScheduledValues(startAt);
    const base = this.musicTargetLevel();
    bus.gain.setTargetAtTime(Math.max(0.0001, target * base), startAt, 0.012);
    bus.gain.setTargetAtTime(this.paused ? 0.0001 : base, startAt + duration, 0.055);
  }

  private sfxFrequency(id: number): number {
    return SFX_FREQUENCIES[id] ?? 440;
  }

  private midiToHz(midi: number): number {
    return 440 * Math.pow(2, (midi - 69) / 12);
  }

  private makeNoiseBuffer(context: AudioContext): AudioBuffer {
    const length = Math.floor(context.sampleRate * 1.2);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    let state = 0x6d2b79f5;
    for (let i = 0; i < length; i += 1) {
      state = (Math.imul(state ^ (state >>> 15), 1 | state) + 0x6d2b79f5) | 0;
      data[i] = ((state >>> 1) / 0x3fffffff) - 1;
    }
    return buffer;
  }
}
